import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useSession, assetUrl } from "@/contexts/SessionContext";
import { PaperClip, ConfidentialStamp, Fade } from "@/components/Emblem";
import { MapPin, Clock, Calendar, FileText, ArrowLeft, Fingerprint, Users, Camera, Mic, StickyNote, Flag, FlaskConical, Layers, User as UserIcon, Eye, EyeOff, Lock, Key, Play, Pause, Square, CheckCircle2, XCircle, Trash2, Undo2, History, Radio } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { sfx } from "@/lib/sounds";
import { ForensicLab } from "@/components/ForensicLab";
import { CCTVControlRoom } from "@/components/CCTVControlRoom";
import { InterrogationRoom } from "@/components/InterrogationRoom";
import { AIInterrogationPanel } from "@/components/AIInterrogationPanel";
import CinematicRevealOverlay from "@/components/CinematicRevealOverlay";
import { LeaderboardPanel, awardCaseScore } from "@/components/Leaderboard";
import { CaseRankingPodium } from "@/components/CaseRankingPodium";
import { DifficultySelector, easyHints, useCaseDifficulty } from "@/components/DifficultySelector";
import { EvidenceMediaModal } from "@/components/EvidenceMedia";
import { hasRichMedia, getEvidenceMedia, MEDIA_TYPE_LABELS } from "@/lib/evidenceMedia";
import { TikTokConnectStrip, InvestigatorMeetingRoom } from "@/components/InvestigatorMeetingRoom";
import { StreamPlatformsManager } from "@/components/streamer/StreamPlatformsManager";
import { useAuth } from "@/pages/AuthPage";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "نظرة عامة", icon: FileText },
  { id: "victim", label: "الضحية", icon: UserIcon },
  { id: "suspects", label: "المشتبه بهم", icon: Users },
  { id: "evidence", label: "الأدلة", icon: Fingerprint },
  { id: "interrogations", label: "الاستجوابات", icon: Mic },
  { id: "forensics", label: "التحليل الجنائي", icon: FlaskConical },
  { id: "cctv", label: "كاميرات المراقبة", icon: Camera },
  { id: "timeline", label: "الخط الزمني", icon: Clock },
  { id: "locations", label: "المواقع", icon: MapPin },
  { id: "board", label: "لوحة التحقيق", icon: Layers },
  { id: "notes", label: "الملاحظات", icon: StickyNote },
  { id: "ops-room", label: "غرفة العمليات", icon: Radio },
  { id: "final-report", label: "الحل النهائي", icon: Flag },
];

export default function CaseDetail() {
  const { caseId } = useParams();
  const { session, updateSession, reveal, unreveal, revealAll, resetCase } = useSession();
  const { user, isOwner } = useAuth();
  const [c, setC] = useState(null);

  // Case-access guard: if user has a specific assigned_cases list or case is locked, block others.
  const [caseRelease, setCaseRelease] = useState(null);
  useEffect(() => { api.get("/case-release").then(r => setCaseRelease(r.data || {})).catch(() => setCaseRelease({})); }, []);
  const rel = caseRelease?.[caseId] || {};
  const isCaseLocked = Boolean(rel.is_locked || rel.status === "UPCOMING_LOCKED" || (rel.available === false && rel.status !== "HIDDEN"));
  const caseAllowed = React.useMemo(() => {
    if (!user || isOwner) return true;
    const a = user.assigned_cases;
    const assigned = (a == null || a === "all") ? true : (Array.isArray(a) && a.includes(caseId));
    if (!assigned) return false;
    if (caseRelease && (isCaseLocked || (caseRelease[caseId] || {}).available === false)) return false;
    return true;
  }, [user, isOwner, caseId, caseRelease, isCaseLocked]);
  // Read initial tab from URL hash (e.g. /case/case-001#ops-room) so the persistent
  // top-bar TikTok badge can deep-link into the Operations Room from any page.
  const initialTab = (() => {
    if (typeof window === "undefined") return "overview";
    const h = window.location.hash.replace("#", "").trim();
    // Do not inherit a stale final-report hash when opening a case.
    // The final solution must be opened deliberately from the tab bar.
    const known = ["overview","victim","suspects","evidence","interrogations","forensics","cctv","timeline","locations","board","notes","ops-room"];
    return known.includes(h) ? h : "overview";
  })();
  const [tab, setTab] = useState(initialTab);
  const [selectedSuspect, setSelectedSuspect] = useState(null);
  // Phase 1: visible per-case reset (POST /api/session/case-full-reset/{case_id}).
  const [confirmCaseReset, setConfirmCaseReset] = useState(false);

  // React to hash changes (host navigates via badge while on same case)
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace("#", "").trim();
      // Ignore stale final-report hashes; keep that tab explicitly user-driven.
      const known = ["overview","victim","suspects","evidence","interrogations","forensics","cctv","timeline","locations","board","notes","ops-room"];
      if (known.includes(h)) setTab(h);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoadError(null); setC(null);
    api.get(`/cases/${caseId}`)
      .then((r) => {
        if (!alive) return;
        if (!r || !r.data || typeof r.data !== "object") {
          setLoadError("empty");
          return;
        }
        setC(r.data);
        try { sfx.fileOpen(); } catch {}
      })
      .catch((err) => {
        if (!alive) return;
        const s = err?.response?.status;
        setLoadError(s === 404 ? "not_found"
                     : s === 403 ? "forbidden"
                     : "unknown");
      });
    return () => { alive = false; };
  }, [caseId]);
  useEffect(() => {
    if (c && session?.current_case_id !== caseId) updateSession({ current_case_id: caseId });
  }, [c, caseId]); // eslint-disable-line

  const streamMode = session?.stream_mode;
  const revealed = session?.revealed?.[caseId] || {};
  const revSet = (kind) => new Set(revealed[kind] || []);
  const revE = revSet("evidence"), revS = revSet("suspects"),
        revL = revSet("locations"), revT = revSet("timeline"),
        revI = revSet("interrogations"), revF = revSet("forensics"),
        revC = revSet("cctv");

  const difficulty = useCaseDifficulty(caseId);

  if (!caseAllowed || loadError === "forbidden") {
    return (
      <Shell>
        <div className="max-w-lg mx-auto text-center py-16 px-4" data-testid="case-load-error">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gold/10 border-2 border-gold/40 flex items-center justify-center text-gold shadow-[0_0_30px_rgba(217,166,80,0.2)]">
            <Lock className="w-10 h-10 text-gold" />
          </div>
          <div className="inline-block px-3 py-1 mb-3 bg-black/80 border border-gold/70 text-gold text-xs font-cairo font-bold rounded-full">
            قريباً · COMING SOON
          </div>
          <div className="text-white text-3xl font-cairo font-black mb-3">القضية مقفلة حالياً</div>
          <div className="text-zinc-300 font-cairo text-sm leading-relaxed mb-6 max-w-md mx-auto">
            يعمل فريق التحقيق على استكمال ملفات هذه القضية وتجهيز مسرح الجريمة، وستكون متاحة لجميع المحققين قريباً جداً.
          </div>
          <div className="flex items-center justify-center gap-3">
            <Link to="/cases" data-testid="case-load-back"
                  className="px-6 py-2.5 bg-gradient-to-r from-gold/90 to-amber-600 text-black font-bold text-sm font-cairo rounded hover:brightness-110 shadow-lg shadow-gold/20 transition-all flex items-center gap-2">
              <ArrowLeft size={14} /> العودة إلى القضايا المتاحة
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (loadError) {
    const msg = loadError === "not_found" ? "القضية غير موجودة أو لم تُنشر بعد."
              : "تعذّر تحميل القضية.";
    return (
      <Shell>
        <div className="max-w-lg mx-auto text-center py-16" data-testid="case-load-error">
          <div className="text-red-400 text-2xl font-cairo mb-3">⚠️ خطأ في التحميل</div>
          <div className="text-zinc-300 font-cairo text-sm mb-6">{msg}</div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => { setLoadError(null); setC(null); }} data-testid="case-load-retry"
                    className="px-4 py-2 border border-gold text-gold text-[12px] font-cairo hover:bg-[rgba(197,160,89,0.08)]">
              إعادة المحاولة
            </button>
            <Link to="/cases" data-testid="case-load-back"
                  className="px-4 py-2 border border-metal text-zinc-300 text-[12px] font-cairo hover:text-white">
              العودة إلى القضايا
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (!c) return (
    <Shell>
      <div className="p-8 max-w-3xl mx-auto" data-testid="case-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 bg-neutral-800/50 rounded"/>
          <div className="h-64 w-full bg-neutral-800/40 rounded"/>
          <div className="h-4 w-1/2 bg-neutral-800/40 rounded"/>
          <div className="h-4 w-2/3 bg-neutral-800/40 rounded"/>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="h-24 bg-neutral-800/40 rounded"/>
            <div className="h-24 bg-neutral-800/40 rounded"/>
            <div className="h-24 bg-neutral-800/40 rounded"/>
          </div>
        </div>
        <div className="text-gold text-sm font-cairo text-center mt-6">جاري تحميل ملف القضية…</div>
      </div>
    </Shell>
  );

  if (!caseAllowed) {
    return (
      <Shell>
        <div className="max-w-lg mx-auto text-center py-16" data-testid="case-access-denied">
          <div className="text-red-400 text-2xl font-cairo mb-3">🚫 الوصول مرفوض</div>
          <div className="text-zinc-400 font-cairo text-sm">هذه القضية غير مخصصة لحسابك. راجع OWNER لطلب الوصول.</div>
          <Link to="/" className="inline-block mt-6 border border-gold text-gold px-4 py-2 text-[12px] font-cairo">العودة إلى الصفحة الرئيسية</Link>
        </div>
      </Shell>
    );
  }

  // In stream mode, only show revealed items
  const filt = (list, revSet, idKey = "id") => streamMode ? list.filter((x) => revSet.has(x[idKey])) : list;

  const plan = String(user?.membership_plan || "").toUpperCase();
  const canControlSession = isOwner || plan === "STREAMER" || String(user?.role || "").toUpperCase() === "STREAMER";
  const hostPanel = canControlSession ? (
    <>
      {/* P5: DifficultySelector removed from the per-case host panel.
          Difficulty now lives on the case document itself and is edited via
          Case Builder (create/edit form). The runtime override remains
          available at /api/session/case-difficulty/{id} but is not surfaced
          as a global control anymore. */}
      <HostControlsForCase c={c} session={session} caseId={caseId} tab={tab}
        revealed={revealed} onReveal={reveal} onUnreveal={unreveal} onRevealAll={revealAll} />
    </>
  ) : null;

  return (
    <Shell hostPanel={hostPanel}>
      <CinematicRevealOverlay caseId={caseId} />
      <Fade>
        {/* Breadcrumbs + visible case reset */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Link to="/" className="hover:text-gold">الرئيسية</Link>
            <ArrowLeft size={12} />
            <Link to="/cases" className="hover:text-gold">القضايا</Link>
            <ArrowLeft size={12} />
            <span className="text-zinc-300 font-cairo">{c.title}</span>
          </div>
          {!confirmCaseReset ? (
            <button onClick={() => setConfirmCaseReset(true)} data-testid="reset-case-btn"
              className="text-[11px] font-cairo text-zinc-400 hover:text-red-400 border border-metal hover:border-red-900/60 px-3 py-1.5 shrink-0">
              إعادة تعيين القضية
            </button>
          ) : (
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={async () => { const ok = await resetCase(caseId); if (ok) { toast.success("تم تصفير القضية — بدأت من الصفر"); setConfirmCaseReset(false); } }} data-testid="reset-case-confirm"
                className="text-[11px] font-cairo px-3 py-1.5 bg-red-900/60 border border-red-700 text-red-200 hover:bg-red-800">
                تأكيد التصفير
              </button>
              <button onClick={() => setConfirmCaseReset(false)} data-testid="reset-case-cancel"
                className="text-[11px] font-cairo px-3 py-1.5 border border-metal text-zinc-400 hover:text-white">
                تراجع
              </button>
            </div>
          )}
        </div>

        {/* ============ REFERENCE-2 HERO LAYOUT ============ */}
        <CaseHero c={c} difficulty={difficulty} />

        {/* Suspects hero-grid — 8 dossier cards */}
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px bg-gradient-to-l from-gold/50 to-transparent flex-1" />
            <div className="text-xs tracking-[0.3em] text-gold font-cairo">المشتبه بهم الثمانية</div>
            <div className="h-px bg-gradient-to-r from-gold/50 to-transparent flex-1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filt(c?.suspects || [], revS).map((s, i) => (
              <SuspectDossierCard key={s.id} s={s} index={i} onClick={() => setSelectedSuspect(s)} revealed={!streamMode || revS.has(s.id)} />
            ))}
            {streamMode && filt(c?.suspects || [], revS).length === 0 && (
              <div className="col-span-full text-center py-10 text-zinc-600">المشتبه بهم مخفيون — بانتظار كشف المضيف.</div>
            )}
          </div>
        </div>

        {/* Easy-mode Hints (does not reveal culprit; only guides investigation) */}
        {difficulty === "easy" && (easyHints[caseId] || []).length > 0 && (
          <div className="mt-8 panel border-emerald-800 p-4 bg-emerald-950/10" data-testid="easy-hints">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] tracking-[0.4em] text-emerald-400 font-mono-ar">EASY MODE · تلميحات المحقق</span>
            </div>
            <ul className="space-y-1 text-xs text-zinc-300 leading-relaxed">
              {easyHints[caseId].map((h, i) => <li key={i}>• {h}</li>)}
            </ul>
          </div>
        )}

        {/* Tabs bar */}
        <div className="mt-10 flex overflow-x-auto border-b border-metal no-scrollbar">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => { setTab(id); sfx.tabSwitch(); }} data-testid={`tab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${tab === id ? "tab-active" : "text-zinc-500 hover:text-white"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="mt-6">
          {tab === "overview" && <OverviewTab c={c} revealed={revealed} />}
          {tab === "victim" && <VictimTab v={c?.victim} />}
          {tab === "suspects" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filt(c?.suspects || [], revS).map((s, i) => <SuspectDossierCard key={s.id} s={s} index={i} onClick={() => setSelectedSuspect(s)} />)}
            </div>
          )}
          {tab === "evidence" && <EvidenceGrid list={filt(c?.evidence || [], revE)} caseId={caseId} caseCover={c?.cover} />}
          {tab === "interrogations" && <InterrogationsTab c={c} revealed={revI} streamMode={streamMode} caseId={caseId} />}
          {tab === "forensics" && <ForensicsTab c={c} revealed={revF} streamMode={streamMode} />}
          {tab === "cctv" && <CCTVTab list={filt(c?.cctv || c?.cctv_cameras || [], revC, "id")} caseId={caseId} suspects={c?.suspects || []} />}
          {tab === "timeline" && <TimelineTab list={c?.timeline || []} revealed={revT} streamMode={streamMode} />}
          {tab === "locations" && <LocationsTab list={filt(c?.locations || [], revL)} />}
          {tab === "board" && <BoardTab c={c} revealed={revealed} />}
          {tab === "notes" && <NotesTab caseId={caseId} c={c} />}
          {tab === "ops-room" && <OperationsRoomTab c={c} caseId={caseId} />}
          {tab === "final-report" && <FinalRevealTab caseId={caseId} c={c} />}
        </div>

        {/* Per-case ownership footer */}
        <div className="mt-10 pt-4 border-t border-metal/40 text-center" data-testid="case-footer">
          <div className="text-[10px] text-gold tracking-[0.3em] font-cairo">OK FBI INVESTIGATION GAME</div>
          <div className="text-[9px] text-zinc-500 mt-1 font-cairo">CREATED BY // AMW</div>
        </div>
      </Fade>

      {/* Suspect dossier modal */}
      <AnimatePresence>
        {selectedSuspect && <SuspectModal s={selectedSuspect} onClose={() => setSelectedSuspect(null)} />}
      </AnimatePresence>
    </Shell>
  );
}

// ================== CASE HERO (matches reference 2) ==================
function CaseHero({ c, difficulty }) {
  const codeStr = String(c?.code || "");
  const caseNumber = codeStr.split("-").pop()?.replace(/[^0-9]/g, "") || codeStr || c?.id || "";
  const isCase1 = c?.id === "case-001";
  const summaryText = c?.summary || c?.description || "";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr_280px] gap-4 lg:gap-5">
      {/* LEFT — big paper folder with case number */}
      <div className="relative">
        <div className="paper-file min-h-[380px] relative overflow-hidden" style={{ transform: "rotate(-1.2deg)" }}>
          <PaperClip className="absolute -top-2 right-8 z-10" />
          {/* Layered background papers */}
          <div className="absolute -bottom-2 -left-2 w-full h-full bg-[#C7BCA1] border border-[#8B7333]/30 -z-10" style={{ transform: "rotate(2deg)" }} />
          <div className="relative z-10">
            <div className="text-[10px] tracking-[0.3em] text-[#5B4B22] mb-1">CASE FILE</div>
            <div className="text-[10px] text-[#5B4B22] mb-6">ملف القضية</div>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-cairo text-[7rem] leading-[0.85] font-black text-[#465C7A] tracking-tight" style={{ textShadow: "2px 2px 0 rgba(0,0,0,0.05)" }}>{caseNumber}</span>
              <div className="flex-1">
                <h1 className="font-cairo text-4xl lg:text-5xl font-black text-[#22201A] leading-tight">{c?.title || "قضية"}</h1>
                <div className="text-sm text-[#465C7A] font-cairo mt-1 font-bold">{c?.subtitle || ""}</div>
              </div>
            </div>
            <div className="border-t border-[#8B7333]/40 my-3" />
            <p className="text-xs leading-relaxed text-[#22201A]/85" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
              {summaryText}
            </p>
            <div className="mt-4 flex items-center justify-between">
              <div className="w-16 h-16 rounded-full border-2 border-[#8B7333]/60 flex items-center justify-center text-[#8B7333] text-[9px] font-bold" style={{ transform: "rotate(-8deg)" }}>
                <div className="text-center leading-tight">
                  <div>ROYAL VIEW</div>
                  <div>EST. 2010</div>
                </div>
              </div>
              <ConfidentialStamp />
            </div>
          </div>
        </div>
      </div>

      {/* CENTER — cinematic hotel corridor / crime scene image */}
      <div className="relative panel overflow-hidden min-h-[380px] bg-zinc-950">
        <img src={assetUrl(c?.scene || c?.cover) || "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"} alt={c?.title || ""}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute inset-0 border border-white/5" />
        {/* Corner brackets */}
        <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-gold" />
        <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-gold" />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-gold" />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-gold" />
        {/* Scene label */}
        <div className="absolute bottom-4 right-4 left-4 flex items-center justify-between">
          <div className="bg-black/70 backdrop-blur px-3 py-1.5 text-[10px] font-mono-ar text-cyan-400 border border-cyan-900/50">SCENE 01 — CRIME SITE</div>
          <div className="bg-black/70 backdrop-blur px-3 py-1.5 text-[10px] font-mono-ar text-gold">{c?.time || ""}</div>
        </div>
      </div>

      {/* RIGHT — info panel with location/time/date/notes */}
      <div className="space-y-3">
        <div className={`px-3 py-2 border rounded-sm text-xs flex items-center justify-between ${difficulty === "easy" ? "border-emerald-700 text-emerald-400" : difficulty === "hard" ? "border-red-700 text-red-400" : "border-gold text-gold"}`} data-testid="difficulty-badge">
          <span className="font-cairo">مستوى الصعوبة</span>
          <span className="font-mono-ar text-[10px]">{difficulty === "easy" ? "EASY · سهل" : difficulty === "hard" ? "HARD · صعب" : "MEDIUM · متوسط"}</span>
        </div>
        <InfoRow icon={MapPin} title="المكان" value={c?.location || "—"} />
        <InfoRow icon={Clock} title="الوقت" value={c?.time || "—"} />
        <InfoRow icon={Calendar} title="تاريخ الحادثة" value={c?.date || "—"} />
        <div className="panel p-4 border-metal">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={14} className="text-gold" />
            <span className="text-[10px] tracking-widest text-gold font-mono-ar">ملاحظة</span>
          </div>
          <ul className="space-y-1 text-xs text-zinc-300 leading-relaxed">
            {isCase1 ? <>
              <li>• الجناح 1307 مغلق من الداخل</li>
              <li>• لا توجد آثار كسر</li>
              <li>• كاميرا الممر منقطعة 6 دقائق</li>
            </> : (summaryText ? <li>• {summaryText.slice(0, 100)}…</li> : <li>• لا توجد ملاحظات إضافية</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, title, value }) {
  return (
    <div className="panel p-3.5 border-metal flex items-start gap-3">
      <div className="w-8 h-8 rounded-sm bg-[rgba(70,92,122,0.15)] border border-[rgba(107,138,184,0.3)] flex items-center justify-center shrink-0">
        <Icon size={16} className="text-steel" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-widest text-zinc-500 font-mono-ar">{title}</div>
        <div className="text-sm text-white mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}

// ================== SUSPECT DOSSIER CARD (matches reference 2 style) ==================
function SuspectDossierCard({ s, index, onClick }) {
  return (
    <button onClick={onClick} data-testid={`suspect-${s.id}`}
      className="text-right group relative bg-gradient-to-b from-[#161619] to-[#0C0C0F] border border-metal rounded-sm p-3 hover:border-gold transition-all duration-200 hover:-translate-y-0.5">
      {/* Index number top corner */}
      <div className="absolute -top-2 -right-2 bg-black border border-gold text-gold text-[10px] px-1.5 py-0.5 font-mono-ar z-10">
        {String(index + 1).padStart(2, "0")}
      </div>
      {/* Paper clip decoration */}
      <PaperClip className="absolute -top-3 right-8 z-20" />

      <div className="flex gap-3">
        {/* Portrait */}
        <div className="w-24 h-32 shrink-0 border border-metal overflow-hidden bg-zinc-900">
          <img src={assetUrl(s.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"} alt={s.name}
            className="w-full h-full object-cover grayscale-[0.15] group-hover:grayscale-0 transition-all"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }} />
        </div>
        {/* Info card (paper-like) */}
        <div className="flex-1 min-w-0 bg-[#E8DFC7] text-[#22201A] rounded-sm p-2.5 relative" style={{ boxShadow: "inset 0 0 20px rgba(0,0,0,0.05)" }}>
          <div className="font-cairo text-lg font-bold leading-tight">{s.name}</div>
          <div className="text-[11px] text-[#465C7A] font-cairo mt-0.5 leading-tight">{s.job}</div>
          <div className="mt-2 text-[10px] text-[#5B4B22]/80">العمر: {s.age}</div>
          <div className="mt-1 text-[10px] text-[#22201A]/70 line-clamp-2 leading-snug">{s.relation}</div>
          <div className="absolute bottom-1 left-1.5 text-[8px] font-mono text-[#5B4B22]/50">ID: {s.id}</div>
        </div>
      </div>
    </button>
  );
}

// ================== SUSPECT MODAL (full dossier) ==================
function SuspectModal({ s, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
        onClick={(e) => e.stopPropagation()}
        className="panel gold-border max-w-4xl w-full max-h-[90vh] overflow-y-auto grid grid-cols-1 md:grid-cols-[280px_1fr]">
        <div className="bg-black">
          <img src={assetUrl(s.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"} className="w-full aspect-[3/4] object-cover" alt={s.name} onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }} />
          <div className="p-4">
            <div className="font-cairo text-2xl font-bold text-white">{s.name}</div>
            <div className="text-xs text-gold mt-1">{s.job}</div>
            <div className="text-[10px] text-zinc-500 mt-2 font-mono-ar">FILE ID: {s.id}</div>
            <div className="mt-3 stat-chip text-[10px]">اشتباه: {s.suspicion}</div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="label-caps mb-1">ملف مشتبه به</div>
              <div className="text-[10px] text-zinc-600 font-mono-ar">CLASSIFIED · SUSPECT DOSSIER</div>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-gold text-xl" data-testid="close-suspect-modal">×</button>
          </div>
          <DetailBlock title="العلاقة بالقضية" content={s.relation || "—"} />
          <DetailBlock title="الدافع المحتمل" content={s.motive || "—"} />
          <DetailBlock title="العذر" content={`"${s.alibi?.claim || (typeof s.alibi === 'string' ? s.alibi : '—')}"`} sub={`الحالة: ${s.alibi?.status || 'غير محدد'}`} />
          <div>
            <div className="label-caps mb-2">الخط الزمني</div>
            <ul className="space-y-1 text-xs">
              {(s.timeline || []).map((t, i) => (
                <li key={i} className="flex gap-2 text-zinc-300">
                  <span className="text-gold font-mono-ar w-16 shrink-0">{Array.isArray(t) ? t[0] : (t?.time || t?.at || "")}</span>
                  <span>{Array.isArray(t) ? t[1] : (t?.event || "")}</span>
                </li>
              ))}
            </ul>
          </div>
          <DetailBlock title="السر" content={s.secret || "—"} muted />
          <div className="text-[10px] text-zinc-600 border-t border-metal pt-3">العمر: {s.age || "—"} سنة</div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const DetailBlock = ({ title, content, sub, muted }) => (
  <div>
    <div className="label-caps mb-1">{title}</div>
    <div className={`text-sm ${muted ? "text-zinc-500 italic" : "text-zinc-200"}`}>{content}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
  </div>
);

// ================== OVERVIEW / VICTIM / EVIDENCE / TABS ==================
function OverviewTab({ c, revealed }) {
  const counts = { e: (revealed?.evidence || []).length, s: (revealed?.suspects || []).length, l: (revealed?.locations || []).length, t: (revealed?.timeline || []).length };
  const summaryText = c?.summary || c?.description || "لا يوجد ملخص متاح لهذه القضية.";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="paper-file"><h3 className="font-cairo text-2xl font-bold mb-3">ملخص التحقيق</h3><p className="text-sm leading-relaxed">{summaryText}</p><div className="mt-4 text-xs opacity-70">فُتح الملف بتاريخ {c?.date || "—"} — {c?.time || "—"}</div></div>
      <div className="panel p-6">
        <div className="label-caps mb-4">تقدم الكشوفات</div>
        <div className="grid grid-cols-2 gap-4">
          <StatCell label="أدلة مكشوفة" val={`${counts.e}/${(c?.evidence || []).length}`} />
          <StatCell label="مشتبه بهم مكشوفون" val={`${counts.s}/${(c?.suspects || []).length}`} />
          <StatCell label="مواقع مفتوحة" val={`${counts.l}/${(c?.locations || []).length}`} />
          <StatCell label="أحداث زمنية" val={`${counts.t}/${(c?.timeline || []).length}`} />
        </div>
      </div>
    </div>
  );
}

const StatCell = ({ label, val }) => (
  <div className="panel p-3 text-center border-metal">
    <div className="text-2xl font-cairo text-gold font-mono-ar">{val}</div>
    <div className="text-[10px] text-zinc-500 mt-1">{label}</div>
  </div>
);

function VictimTab({ v }) {
  if (!v) {
    return <div className="panel p-8 text-center text-zinc-500 font-cairo">لا توجد بيانات ضحية مسجلة في هذا الملف.</div>;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="panel overflow-hidden">
        <div className="w-full aspect-square bg-zinc-950 grid place-items-center text-zinc-700"><UserIcon size={64} strokeWidth={0.8} /></div>
        <div className="p-5">
          <div className="font-cairo text-2xl text-white">{v.name || "مجهول"}</div>
          <div className="text-xs text-gold mt-1">{v.file_id || ""}</div>
          <div className="text-sm text-zinc-400 mt-2">{v.job || ""}</div>
        </div>
      </div>
      <div className="panel p-6 lg:col-span-2 space-y-3 text-sm">
        <Row k="العمر" v={v.age || "—"} />
        <Row k="الحالة الاجتماعية" v={v.marital || "—"} />
        <Row k="الخلفية" v={v.background || "—"} />
        <Row k="الوضع المالي" v={v.financial || "—"} />
        <Row k="سبب محتمل للاستهداف" v={v.reason || "—"} />
        <div className="pt-3 border-t border-metal">
          <div className="label-caps mb-2">آخر التحركات</div>
          <ul className="space-y-1 text-zinc-300 text-xs">{(v.last_moves || []).map((m, i) => <li key={i} className="flex gap-2"><span className="text-gold">•</span>{m}</li>)}</ul>
        </div>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => <div className="flex justify-between border-b border-metal pb-2"><span className="text-zinc-500">{k}</span><span className="text-white">{v}</span></div>;

function EvidenceCard({ e, caseId, onExamine, onUnlocked }) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const media = getEvidenceMedia(caseId, e.id);
  const label = media ? MEDIA_TYPE_LABELS[media.media_type] : null;
  const isLocked = Boolean(e.pin_locked && !e.description);

  const unlockPin = async () => {
    const p = pin.trim();
    if (!p) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/cases/${caseId}/evidence/${e.id}/unlock`, { pin: p });
      if (data.ok) {
        toast.success("تم فك شفرة الدليل بنجاح!");
        setShowPin(false);
        setPin("");
        onUnlocked?.(e.id, data.evidence);
      } else {
        setErr(data.error || "الرمز غير صحيح");
      }
    } catch (exc) {
      setErr(exc?.response?.data?.detail || "فشل فك شفرة الدليل");
    } finally {
      setBusy(false);
    }
  };

  if (isLocked) {
    return (
      <div
        key={e.id}
        data-testid={`evidence-${e.id}`}
        className="panel p-4 border-amber-600/40 bg-amber-950/15 flex flex-col justify-between"
      >
        <div>
          <div className="flex items-start justify-between mb-2">
            <span className="evidence-tag">{e.id}</span>
            <span className="text-[10px] text-amber-400 font-bold flex items-center gap-1 font-mono-ar">
              <Lock size={12} /> مقفول برمز
            </span>
          </div>
          <div className="font-cairo text-lg text-amber-200 mb-1">{e.name}</div>
          {e.pin_hint && (
            <div className="text-xs text-amber-300/90 bg-amber-950/40 border border-amber-800/40 p-2 rounded mb-3 font-cairo">
              💡 تلميح: {e.pin_hint}
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-amber-900/40 pt-3">
          {showPin ? (
            <div className="space-y-2">
              <input
                type="text"
                value={pin}
                onChange={(ev) => setPin(ev.target.value)}
                onKeyDown={(ev) => ev.key === "Enter" && unlockPin()}
                placeholder="أدخل رمز فك الشفرة..."
                className="w-full bg-black/60 border border-amber-700/60 rounded px-2.5 py-1.5 text-xs text-white placeholder:text-zinc-600 font-mono text-center outline-none focus:border-amber-400"
                autoFocus
                data-testid={`evidence-pin-input-${e.id}`}
              />
              {err && <div className="text-[10px] text-red-400 text-center font-cairo">{err}</div>}
              <div className="flex gap-2">
                <button
                  onClick={unlockPin}
                  disabled={busy || !pin.trim()}
                  className="flex-1 py-1 text-xs font-cairo bg-amber-600 hover:bg-amber-500 text-black font-bold rounded transition-colors"
                  data-testid={`evidence-pin-submit-${e.id}`}
                >
                  {busy ? "جارِ التحقق…" : "فك الشفرة"}
                </button>
                <button
                  onClick={() => {
                    setShowPin(false);
                    setErr("");
                  }}
                  className="px-2.5 py-1 text-xs font-cairo text-zinc-400 hover:text-white border border-zinc-800 rounded"
                >
                  إلغاء
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowPin(true)}
              className="w-full py-1.5 text-xs font-cairo bg-amber-900/40 border border-amber-700 text-amber-300 hover:bg-amber-800/50 rounded flex items-center justify-center gap-1.5 transition-colors"
              data-testid={`evidence-pin-open-${e.id}`}
            >
              <Key size={13} /> فك شفرة الدليل
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      key={e.id}
      data-testid={`evidence-${e.id}`}
      className="panel p-4 border-metal hover:border-gold transition-colors flex flex-col"
    >
      <div className="flex items-start justify-between mb-2">
        <span className="evidence-tag">{e.id}</span>
        <span
          className={`text-[10px] ${
            e.importance === "دليل رئيسي"
              ? "text-red-400"
              : e.importance === "دليل مضلل"
              ? "text-yellow-500"
              : "text-zinc-500"
          }`}
        >
          {e.importance}
        </span>
      </div>
      <div className="font-cairo text-lg text-white mb-1">{e.name}</div>
      <div className="text-[11px] text-zinc-500 mb-2">
        {label ? <span className="text-gold">{label}</span> : e.type}
        {" • "}
        {e.location}
      </div>
      {/* Phase 1: manual upload thumbnail — show the real /api/uploads/... image when present */}
      {e.image && (
        <div className="mb-2 overflow-hidden border border-metal/60 bg-black">
          <img
            src={assetUrl(e.image)}
            alt={e.name}
            loading="lazy"
            data-testid={`evidence-thumb-${e.id}`}
            className="w-full h-32 object-cover"
            onError={(ev) => { ev.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}
      <p className="text-xs text-zinc-400 leading-relaxed flex-1">{e.description}</p>
      {e.analysis && (
        <div className="mt-3 p-2.5 bg-black/40 border border-cyan-900/50 text-cyan-300 text-[11px] font-mono-ar">
          ✓ {e.analysis.result}
        </div>
      )}
      <button
        onClick={() => onExamine(e)}
        data-testid={`evidence-examine-${e.id}`}
        className="mt-3 w-full text-[11px] font-cairo px-3 py-1.5 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center justify-center gap-1.5"
      >
        <Eye size={12} /> فحص الدليل
      </button>
    </div>
  );
}

function EvidenceGrid({ list, caseId, caseCover }) {
  const [active, setActive] = useState(null);
  const [items, setItems] = useState(list);

  useEffect(() => {
    setItems(list);
  }, [list]);

  const handleUnlocked = (id, newEv) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              ...newEv,
              description: newEv.description || item.description,
              pin_locked: false,
            }
          : item
      )
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((e) => (
        <EvidenceCard
          key={e.id}
          e={e}
          caseId={caseId}
          onExamine={setActive}
          onUnlocked={handleUnlocked}
        />
      ))}
      {items.length === 0 && (
        <div className="col-span-full text-center text-zinc-600 py-10">
          لم يُكشف عن أي دليل بعد.
        </div>
      )}
      <EvidenceMediaModal
        open={!!active}
        onClose={() => setActive(null)}
        evidence={active}
        caseId={caseId}
        caseCover={caseCover}
      />
    </div>
  );
}

function InterrogationsTab({ c, revealed, streamMode, caseId }) {
  const [active, setActive] = useState(null); // suspect object for OWNER-scripted Q&A
  const [aiActive, setAiActive] = useState(null); // suspect object for AI interrogation
  return (
    <div className="space-y-4">
      <div className="panel border-gold p-5 flex flex-col md:flex-row items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">INTERROGATION ROOM</div>
          <div className="font-cairo text-2xl text-white mt-1">غرفة الاستجواب</div>
          <div className="text-xs text-zinc-400 mt-1">اطرح أسئلتك بحرية على المشتبه به مباشرةً عبر <span className="text-gold">الاستجواب الذكي</span> — أو استخدم الأسئلة المُعدّة مسبقاً في وضع البث.</div>
        </div>
      </div>
      {(c?.suspects || []).filter(s => !streamMode || revealed.has(s.id)).map((s) => (
        <div key={s.id} className="panel p-5 flex gap-4" data-testid={`interrogation-suspect-${s.id}`}>
          <div className="w-24 h-32 border border-metal overflow-hidden shrink-0 bg-zinc-900"><img src={assetUrl(s.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"} alt={s.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }} /></div>
          <div className="flex-1">
            <div className="font-cairo text-xl text-white">{s.name}</div>
            <div className="text-xs text-gold mb-2">{s.job}</div>
            <div className="text-xs text-zinc-500 mb-2">العذر: {s.alibi?.claim || (typeof s.alibi === 'string' ? s.alibi : '—')} <span className="text-red-400">({s.alibi?.status || ''})</span></div>
            <div className="text-xs text-zinc-400">{s.secret}</div>
          </div>
          <div className="flex flex-col justify-center gap-2">
            <button onClick={() => setAiActive(s)} data-testid={`ai-interrogation-start-${s.id}`}
              className="text-xs py-2 px-4 whitespace-nowrap font-cairo bg-gold text-black hover:bg-yellow-500 transition-colors flex items-center gap-1.5">
              <span>استجواب ذكي</span>
              <span className="text-[9px] tracking-widest opacity-70">AI</span>
            </button>
            <button onClick={() => setActive(s)} data-testid={`interrogation-start-${s.id}`}
              className="text-xs py-2 px-4 whitespace-nowrap font-cairo border border-metal text-zinc-300 hover:border-gold hover:text-gold transition-colors">أسئلة البث</button>
          </div>
        </div>
      ))}
      <InterrogationRoom open={!!active} onClose={() => setActive(null)} caseId={caseId} suspect={active} />
      <AIInterrogationPanel open={!!aiActive} onClose={() => setAiActive(null)} caseId={caseId} suspect={aiActive} />
    </div>
  );
}

function ForensicsTab({ c, revealed, streamMode }) {
  const [lab, setLab] = useState(null);
  const { user, isOwner } = useAuth();
  const plan = user?.membership_plan || (isOwner ? "OWNER" : "FREE");
  const isVipOrHigher = isOwner || ["VIP", "LEGENDARY", "ROYAL", "STREAMER", "OWNER"].includes(String(plan).toUpperCase());

  const handleOpenLab = (toolKey) => {
    if (!isVipOrHigher && (toolKey === "dna" || toolKey === "audio")) {
      toast.error("تحليل DNA والصوت مخصص لباقة VIP فقط — يرجى ترقية الباقة للوصول إليهما");
      return;
    }
    setLab(toolKey);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="text-gold" />
          <span className="font-cairo text-sm text-zinc-300 font-bold">المختبر الجنائي والأدلة المتقدمة</span>
        </div>
        {!isVipOrHigher && (
          <Link to="/plans" data-testid="forensics-vip-banner"
                className="text-[11px] font-cairo text-amber-400 bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded flex items-center gap-1.5 hover:bg-amber-500/20 transition-all">
            <Lock size={12} />
            <span>التحليلات الحصرية والمتقدمة (DNA والصوت) تتطلب باقة VIP — ترقية الآن</span>
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[{k:"fingerprint",l:"البصمات"},{k:"dna",l:"DNA"},{k:"audio",l:"الصوت"},{k:"documents",l:"الوثائق"},{k:"phone",l:"الهاتف"},{k:"materials",l:"المواد"}].map(t => {
          const isVipTool = !isVipOrHigher && (t.k === "dna" || t.k === "audio");
          return (
            <button key={t.k} onClick={() => handleOpenLab(t.k)} data-testid={`open-lab-${t.k}`}
              className={`panel border-metal p-4 text-center transition-all hover:-translate-y-0.5 relative group ${isVipTool ? "hover:border-amber-500/60 opacity-80" : "hover:border-gold"}`}>
              {isVipTool && (
                <span className="absolute top-2 left-2 text-[9px] font-mono-ar bg-amber-500/20 border border-amber-500/40 text-amber-400 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Lock size={9} /> VIP
                </span>
              )}
              <FlaskConical size={28} className={`mx-auto mb-2 group-hover:scale-110 transition-transform ${isVipTool ? "text-amber-400" : "text-gold"}`} />
              <div className="text-sm font-cairo text-white">{t.l}</div>
              <div className={`text-[10px] mt-1 font-mono-ar ${isVipTool ? "text-amber-500/80" : "text-zinc-500"}`}>
                {isVipTool ? "VIP LOCKED" : "OPEN LAB"}
              </div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(c?.evidence || []).filter(e => e.analysis && (!streamMode || revealed.has(e.id))).map((e) => (
          <div key={e.id} className="panel p-4 border-metal">
            <div className="flex items-center justify-between mb-2">
              <span className="evidence-tag">{e.id}</span>
              <span className="text-[10px] text-zinc-500 font-mono-ar">TOOL: {e.analysis.tool}</span>
            </div>
            <div className="font-cairo text-lg text-white mb-2">{e.name}</div>
            <div className="p-3 bg-black/40 border border-cyan-900 text-sm text-cyan-300 font-mono-ar">✓ {e.analysis.result}</div>
          </div>
        ))}
      </div>
      {lab && <ForensicLab open onClose={() => setLab(null)} tool={lab} caseId={c.id} suspects={c.suspects} />}
    </div>
  );
}


function CCTVTab({ list, caseId, suspects }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  useEffect(() => {
    if (!caseId) return;
    const load = () => api.get(`/session/cctv-evidence/${caseId}`).then(r => setSaved(r.data || [])).catch(() => setSaved([]));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [caseId, open]);
  return (
    <div>
      <div className="panel border-gold p-6 mb-5 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="cctv-launcher">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 border border-gold/40 bg-black flex items-center justify-center relative">
            <Camera size={40} className="text-gold" strokeWidth={1} />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">SURVEILLANCE WORKSTATION</div>
            <div className="font-cairo text-2xl text-white mt-1">غرفة كاميرات المراقبة</div>
            <div className="text-xs text-zinc-400 mt-1">{list.length} كاميرا · حفظ اللقطات كأدلة · تكبير · تمرير · تحديد المناطق المهمة</div>
          </div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary px-5 py-2 flex items-center gap-2" data-testid="cctv-launch">
          <Play size={14} /> فتح غرفة التحكم
        </button>
      </div>

      {/* Quick preview cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 bg-black p-2 border border-metal mb-5">
        {list.map((cam) => (
          <button key={cam.id} onClick={() => setOpen(true)} data-testid={`cctv-preview-${cam.id}`}
            className="bg-black border border-zinc-900 hover:border-gold aspect-video relative overflow-hidden scanline p-3 flex flex-col justify-between transition-colors">
            <div className="flex justify-between text-[10px] text-cyan-400 font-mono-ar">
              <span>● REC</span><span>{cam.id}</span>
            </div>
            <div className="text-center">
              <Camera size={32} strokeWidth={0.8} className="mx-auto text-zinc-700" />
              <div className="text-[10px] text-zinc-500 mt-1">{cam.location || cam.name}</div>
            </div>
            <div className="text-[9px] text-gold font-mono-ar text-right">{cam.time || ""}</div>
          </button>
        ))}
      </div>

      {/* Saved CCTV Evidence recap */}
      <div>
        <div className="label-caps mb-2">أدلة الكاميرات المحفوظة ({saved.length})</div>
        {saved.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-4">افتح غرفة التحكم والتقط لقطات لحفظها كأدلة.</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {saved.map(ev => {
            const sus = suspects.find(s => s.id === ev.suspect_id);
            return (
              <div key={ev.id} className={`panel p-3 border ${ev.pinned ? "border-gold" : "border-metal"} ${ev.revealed ? "" : "opacity-70"}`} data-testid={`case-cctv-ev-${ev.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="evidence-tag">{ev.id}</span>
                  <div className="flex gap-2 text-[10px]">
                    {ev.pinned && <span className="text-gold">⭐ مهم</span>}
                    {ev.revealed ? <span className="text-emerald-400">تم الكشف</span> : <span className="text-zinc-500">مخفي</span>}
                  </div>
                </div>
                <div className="text-xs font-cairo text-white mb-1">{ev.event_label}</div>
                <div className="text-[10px] text-zinc-500 font-mono-ar">{ev.camera_id} · {ev.timestamp}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{ev.camera_name}</div>
                {sus && <div className="mt-2 flex items-center gap-2 border-t border-metal pt-2">
                  <img
                    src={assetUrl(sus.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"}
                    alt=""
                    className="w-8 h-8 object-cover border border-metal"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }}
                  />
                  <span className="text-xs text-gold font-cairo">{sus.name}</span>
                </div>}
              </div>
            );
          })}
        </div>
      </div>

      <CCTVControlRoom open={open} onClose={() => setOpen(false)} caseId={caseId} suspects={suspects} />
    </div>
  );
}

function TimelineTab({ list = [], revealed, streamMode }) {
  return (
    <div className="relative border-r-2 border-gold pr-6 space-y-3">
      {(list || []).map((t, i) => {
        const shown = !streamMode || revealed?.has(String(i));
        if (!shown) return null;
        return (
          <div key={i} className="panel p-4 relative border-metal">
            <div className="absolute right-[-33px] top-4 w-4 h-4 rounded-full bg-gold border-4 border-black" />
            <div className="flex items-center gap-3">
              <div className="text-gold font-mono-ar text-sm w-20 shrink-0">{t.time || t.at || "—"}</div>
              <div className="flex-1 text-white text-sm">{t.event}</div>
              <div className={`text-[10px] px-2 py-0.5 border ${t.certainty === "متناقض" ? "border-red-800 text-red-400" : "border-cyan-800 text-cyan-400"}`}>{t.certainty || "مؤكد"}</div>
            </div>
            {t.source && <div className="text-[10px] text-zinc-500 mt-1 mr-24">المصدر: {t.source}</div>}
          </div>
        );
      })}
    </div>
  );
}

function LocationsTab({ list = [] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(list || []).map((loc) => (
        <div key={loc.id} className="panel overflow-hidden border-metal">
          <div className="w-full aspect-video bg-zinc-950 grid place-items-center text-zinc-800"><MapPin size={40} strokeWidth={0.8} /></div>
          <div className="p-4">
            <div className="font-cairo text-lg text-white">{loc.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{loc.desc || loc.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BoardTab({ c, revealed }) {
  const s = new Set(revealed?.suspects || []);
  const e = new Set(revealed?.evidence || []);
  return (
    <div className="panel p-6 grid-dot-bg min-h-[500px]">
      <div className="label-caps mb-4">لوحة التحقيق — العناصر المكشوفة</div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {(c?.suspects || []).filter(x => s.has(x.id)).map((x) => (
          <div key={x.id} className="panel p-3 gold-border text-center">
            <img
              src={assetUrl(x.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"}
              className="w-16 h-16 rounded-full mx-auto object-cover"
              alt={x.name || ""}
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }}
            />
            <div className="text-xs mt-2 text-white">{x.name}</div>
            <div className="text-[9px] text-gold font-mono-ar">{x.id}</div>
          </div>
        ))}
        {(c?.evidence || []).filter(x => e.has(x.id)).slice(0, 8).map((x) => (
          <div key={x.id} className="panel p-3 border border-cyan-900 text-center">
            <Fingerprint size={20} className="mx-auto text-cyan-400" />
            <div className="text-xs mt-2 text-white">{x.name}</div>
            <div className="text-[9px] text-cyan-400 font-mono-ar">{x.id}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesTab({ caseId, c }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const [color, setColor] = useState("GOLD");
  const [suspectId, setSuspectId] = useState("");
  const [evidenceId, setEvidenceId] = useState("");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // note_id being edited

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/notes`, { params: { case_id: caseId } });
      setNotes(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setText(""); setCategory("GENERAL"); setColor("GOLD");
    setSuspectId(""); setEvidenceId(""); setPinned(false); setEditing(null);
  };

  const submit = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      const payload = {
        case_id: caseId, text: t, category, color,
        suspect_id: suspectId || null, evidence_id: evidenceId || null,
        pinned,
      };
      if (editing) {
        await api.patch(`/notes/${editing}`, payload);
      } else {
        await api.post(`/notes`, payload);
      }
      reset();
      await load();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("note save failed", e);
    } finally { setSaving(false); }
  };

  const startEdit = (n) => {
    setEditing(n.note_id);
    setText(n.text || ""); setCategory(n.category || "GENERAL");
    setColor(n.color || "GOLD"); setSuspectId(n.suspect_id || "");
    setEvidenceId(n.evidence_id || ""); setPinned(!!n.pinned);
  };

  const del = async (note_id) => {
    if (!window.confirm("حذف الملاحظة؟")) return;
    try {
      await api.delete(`/notes/${note_id}`);
      await load();
    } catch {}
  };

  const togglePin = async (n) => {
    try {
      await api.patch(`/notes/${n.note_id}`, { pinned: !n.pinned });
      await load();
    } catch {}
  };

  const suspects = (c?.suspects) || [];
  const evidenceList = (c?.evidence) || [];

  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="max-w-4xl" dir="rtl">
      {/* Editor — simplified per P7 spec */}
      <div className="panel p-5 mb-5 border-metal" data-testid="notes-editor">
        <div className="mb-3">
          <h3 className="text-lg font-cairo font-black text-gold">ملاحظاتي الخاصة</h3>
          <p className="text-xs text-zinc-300 font-cairo mt-0.5">
            هذه الملاحظات خاصة بك داخل هذه القضية.
          </p>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 4000))}
          placeholder="اكتب ملاحظتك هنا..."
          data-testid="note-input"
          className="w-full bg-black/40 border border-metal p-3 text-white outline-none focus:border-gold min-h-32 text-sm font-cairo"
        />
        {/* Primary options (case-scoped links + pin) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-xs font-cairo">
          <select value={suspectId} onChange={(e) => setSuspectId(e.target.value)}
            data-testid="note-suspect"
            className="bg-black/40 border border-metal p-2 text-white outline-none focus:border-gold">
            <option value="">ربط بمشتبه به — اختياري</option>
            {suspects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={evidenceId} onChange={(e) => setEvidenceId(e.target.value)}
            data-testid="note-evidence"
            className="bg-black/40 border border-metal p-2 text-white outline-none focus:border-gold">
            <option value="">ربط بدليل — اختياري</option>
            {evidenceList.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 border border-metal p-2 text-zinc-200 cursor-pointer justify-center">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)}
              data-testid="note-pinned" className="accent-gold" />
            تثبيت
          </label>
        </div>
        {/* Advanced options — collapsed by default */}
        <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
          data-testid="notes-advanced-toggle"
          className="text-[11px] text-zinc-300 hover:text-gold font-cairo mt-3 inline-flex items-center gap-1">
          {showAdvanced ? "▲" : "▼"} خيارات إضافية
        </button>
        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 mt-2 text-xs font-cairo" data-testid="notes-advanced-panel">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              data-testid="note-category"
              className="bg-black/40 border border-metal p-2 text-white outline-none focus:border-gold">
              <option value="GENERAL">نوع: عام</option>
              <option value="SUSPECT">نوع: مشتبه به</option>
              <option value="EVIDENCE">نوع: دليل</option>
              <option value="TIMELINE">نوع: خط زمني</option>
              <option value="THEORY">نوع: نظرية</option>
            </select>
            <select value={color} onChange={(e) => setColor(e.target.value)}
              data-testid="note-color"
              className="bg-black/40 border border-metal p-2 text-white outline-none focus:border-gold">
              <option value="GOLD">لون: ذهبي</option>
              <option value="RED">لون: أحمر</option>
              <option value="AMBER">لون: كهرماني</option>
              <option value="GREEN">لون: أخضر</option>
              <option value="BLUE">لون: أزرق</option>
            </select>
          </div>
        )}
        <div className="flex items-center gap-2 mt-4">
          <button onClick={submit} disabled={saving || !text.trim()} data-testid="add-note"
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed">
            {editing ? "حفظ التعديل" : "حفظ الملاحظة"}
          </button>
          {editing && (
            <button onClick={reset} className="text-xs font-cairo text-zinc-300 hover:text-white px-3 py-1.5 border border-metal">
              إلغاء
            </button>
          )}
          <div className="text-[10px] text-zinc-400 font-cairo mr-auto">{text.length}/4000</div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-zinc-300 font-cairo py-6 text-sm">جاري التحميل…</div>
      ) : notes.length === 0 ? (
        <div className="text-center text-zinc-300 font-cairo py-6 text-sm border border-dashed border-metal">
          لا توجد ملاحظات بعد.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="notes-list">
          {notes.map((n) => <NoteCard key={n.note_id} n={n} suspects={suspects}
            evidenceList={evidenceList} onEdit={startEdit} onDelete={del} onTogglePin={togglePin} />)}
        </div>
      )}
    </div>
  );
}

function NoteCard({ n, suspects, evidenceList, onEdit, onDelete, onTogglePin }) {
  const COLORS = {
    GOLD:  "border-gold/60 bg-yellow-950/20",
    RED:   "border-red-800/60 bg-red-950/20",
    AMBER: "border-amber-800/60 bg-amber-950/20",
    GREEN: "border-emerald-800/60 bg-emerald-950/20",
    BLUE:  "border-blue-800/60 bg-blue-950/20",
  };
  const CAT_LABEL = { GENERAL: "عام", SUSPECT: "مشتبه به", EVIDENCE: "دليل",
                      TIMELINE: "خط زمني", THEORY: "نظرية" };
  const linkedSuspect = suspects.find(s => s.id === n.suspect_id);
  const linkedEvidence = evidenceList.find(e => e.id === n.evidence_id);
  return (
    <div className={`p-4 border ${COLORS[n.color] || COLORS.GOLD}`} data-testid={`note-card-${n.note_id}`}>
      <div className="flex items-center justify-between mb-2 text-[10px] font-cairo">
        <div className="flex items-center gap-2">
          <span className="text-gold tracking-widest">{CAT_LABEL[n.category] || "عام"}</span>
          {n.pinned && <span className="text-amber-400">● مثبتة</span>}
          {n.featured && <span className="text-gold">★ أبرزها المالك</span>}
        </div>
        <div className="text-zinc-500">{new Date(n.updated_at).toLocaleString("ar-EG")}</div>
      </div>
      <div className="text-sm text-zinc-100 font-cairo whitespace-pre-wrap leading-relaxed mb-2">{n.text}</div>
      {(linkedSuspect || linkedEvidence) && (
        <div className="text-[11px] text-zinc-400 font-cairo mb-3 flex gap-2 flex-wrap">
          {linkedSuspect && <span className="px-2 py-0.5 border border-metal">👤 {linkedSuspect.name}</span>}
          {linkedEvidence && <span className="px-2 py-0.5 border border-metal">🔎 {linkedEvidence.name}</span>}
        </div>
      )}
      <div className="flex items-center gap-2 text-[11px] font-cairo">
        <button onClick={() => onTogglePin(n)} className="text-zinc-400 hover:text-amber-400" data-testid={`pin-note-${n.note_id}`}>
          {n.pinned ? "إلغاء التثبيت" : "تثبيت"}
        </button>
        <button onClick={() => onEdit(n)} className="text-zinc-400 hover:text-gold" data-testid={`edit-note-${n.note_id}`}>
          تعديل
        </button>
        <button onClick={() => onDelete(n.note_id)} className="text-zinc-400 hover:text-red-400 ml-auto" data-testid={`remove-note-${n.note_id}`}>
          حذف
        </button>
      </div>
    </div>
  );
}

function FinalRevealTab({ caseId, c }) {
  const { session, accuseSuspect, revealCaseTruth, clearCaseResult, fullCaseReset } = useSession();
  const { hasPerm } = useAuth();
  const canControlFinal = hasPerm("REVEAL_FINAL_SOLUTION");
  const result = (session?.case_results || {})[caseId];  // { status, accused_id, revealed, ... }
  const [selectedSuspect, setSelectedSuspect] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [revealData, setRevealData] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmFullReset, setConfirmFullReset] = useState(false);
  const revealed = session?.revealed?.[caseId] || {};

  const submitAccusation = async () => {
    if (!selectedSuspect) return;
    const votes = session?.votes?.[caseId] || {};
    const correct = c.solution?.culprit; // won't exist on client (stripped)
    const total = Object.keys(votes).length;
    await accuseSuspect(caseId, selectedSuspect, {
      evidence_found: (revealed.evidence || []).length,
      duration_seconds: 0,
      vote_accuracy_pct: 0,
    });
    // Award viewer scores for correct votes on this case (idempotent)
    try { await awardCaseScore(caseId); } catch {}
    setConfirming(false);
  };
  const doReveal = async () => {
    const data = await revealCaseTruth(caseId);
    setRevealData(data);
  };

  // ===== State 1: no result yet — accusation flow =====
  if (!result && !canControlFinal) {
    return (
      <div className="max-w-3xl panel gold-border p-8 text-center" data-testid="final-awaiting-host">
        <Lock size={40} className="mx-auto text-gold mb-3" strokeWidth={1} />
        <h2 className="font-cairo text-3xl text-white">النتيجة بانتظار المضيف</h2>
        <p className="text-sm text-zinc-400 mt-2">لا يملك حسابك صلاحية اعتماد الاتهام النهائي أو كشف الحقيقة.</p>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="max-w-3xl panel gold-border p-8">
        <div className="text-center mb-6">
          <Lock size={40} className="mx-auto text-gold mb-3" strokeWidth={1} />
          <h2 className="font-cairo text-3xl text-white">تقديم الاتهام النهائي</h2>
          <p className="text-sm text-zinc-400 mt-2">اختر المتهم النهائي. بعد التأكيد سيتم تسجيل نتيجة القضية.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          {(c?.suspects || []).map(s => (
            <button key={s.id} onClick={() => setSelectedSuspect(s.id)} data-testid={`accuse-select-${s.id}`}
              className={`text-right p-2 border text-xs rounded-sm transition-all ${selectedSuspect === s.id ? "border-gold bg-[rgba(197,160,89,0.1)] text-gold" : "border-metal text-zinc-400 hover:border-gold"}`}>
              <div className="font-cairo text-sm">{s.name}</div>
              <div className="text-[10px] opacity-70">{s.id}</div>
            </button>
          ))}
        </div>
        {selectedSuspect && !confirming && (
          <button onClick={() => setConfirming(true)} className="btn-primary w-full" data-testid="accuse-init">
            متابعة إلى التأكيد
          </button>
        )}
        {confirming && (
          <div className="border border-red-900 rounded-sm p-4 text-center">
            <div className="text-white mb-2 font-cairo">هل تريد اعتماد <span className="text-gold">{(c?.suspects || []).find(s=>s.id===selectedSuspect)?.name}</span> كمتهم نهائي؟</div>
            <div className="text-xs text-zinc-500 mb-4">لن تتمكن من التراجع إلا عبر «إلغاء نتيجة القضية».</div>
            <div className="flex gap-3 justify-center">
              <button onClick={submitAccusation} className="btn-primary" data-testid="accuse-confirm">تأكيد الاتهام</button>
              <button onClick={() => setConfirming(false)} className="btn-ghost">إلغاء</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== State 2: Result registered — show cinematic screen =====
  const isSolved = result.status === "solved";
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="case-result-screen">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
        className={`panel p-10 text-center relative overflow-hidden ${isSolved ? "border-emerald-700" : "border-red-800"}`}>
        <div className={`absolute inset-0 pointer-events-none ${isSolved ? "bg-gradient-to-b from-emerald-950/30 to-transparent" : "bg-gradient-to-b from-red-950/30 to-transparent"}`} />
        <div className="relative">
          {isSolved ? <CheckCircle2 size={80} className="mx-auto text-emerald-400 mb-3" strokeWidth={1.2} /> : <XCircle size={80} className="mx-auto text-red-400 mb-3" strokeWidth={1.2} />}
          <div className={`text-xs tracking-[0.4em] font-mono-ar mb-2 ${isSolved ? "text-emerald-400" : "text-red-400"}`}>{isSolved ? "CASE SOLVED" : "CASE FAILED"}</div>
          <h2 className="font-cairo text-5xl font-black text-white mb-3">{isSolved ? "تم حل القضية" : "لم يتم حل القضية"}</h2>
          <div className="text-sm text-zinc-400 mb-4">المتهم المختار: <span className="text-gold font-cairo">{result.accused_name}</span> · <span className="text-zinc-500 font-mono-ar">{result.accused_id}</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto text-center">
            <StatCell label="الدقة" val={`${result.accuracy_score}%`} />
            <StatCell label="أدلة مكتشفة" val={`${result.evidence_found}/${result.total_evidence}`} />
            <StatCell label="مدة التحقيق" val={result.duration_seconds ? `${Math.floor(result.duration_seconds/60)}m` : "—"} />
            <StatCell label="دقة التصويت" val={result.vote_accuracy_pct ? `${result.vote_accuracy_pct}%` : "—"} />
          </div>
        </div>
      </motion.div>

      {/* Truth reveal (host controlled) */}
      {canControlFinal && !result.revealed && !revealData && (
        <div className="panel border-gold p-6 text-center">
          <div className="text-sm text-zinc-400 mb-4">الحل الكامل لم يُكشف بعد. اضغط الزر لعرضه للجمهور.</div>
          <button onClick={doReveal} className="btn-primary" data-testid="reveal-truth-btn">كشف الحقيقة الكاملة</button>
        </div>
      )}
      {(revealData || result.revealed) && (
        <TruthRevealDetails caseId={caseId} data={revealData} />
      )}

      {/* Host reset controls */}
      {canControlFinal && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="panel border-metal p-4">
          <div className="label-caps mb-2">إلغاء نتيجة القضية</div>
          <div className="text-[11px] text-zinc-500 mb-3">يزيل ✅/❌ فقط ويعيد البطاقة إلى الحالة المحايدة. لا يمس الأدلة أو التقدم.</div>
          {!confirmClear ? (
            <button onClick={() => setConfirmClear(true)} className="btn-ghost w-full text-xs text-yellow-500 border-yellow-800/60" data-testid="clear-result-init">إلغاء النتيجة</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={async () => { await clearCaseResult(caseId); setConfirmClear(false); setRevealData(null); }} data-testid="clear-result-confirm" className="btn-primary text-xs flex-1">تأكيد</button>
              <button onClick={() => setConfirmClear(false)} className="btn-ghost text-xs flex-1">تراجع</button>
            </div>
          )}
        </div>
        <div className="panel border-metal p-4">
          <div className="label-caps mb-2">إعادة التحقيق بالكامل</div>
          <div className="text-[11px] text-zinc-500 mb-3">يعيد كل شيء إلى الصفر: النتيجة، الأدلة، الأصوات، الاستجوابات، الخط الزمني.</div>
          {!confirmFullReset ? (
            <button onClick={() => setConfirmFullReset(true)} className="btn-ghost w-full text-xs text-red-400 border-red-900/60" data-testid="full-reset-init">إعادة تحقيق كاملة</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={async () => { const ok = await fullCaseReset(caseId); if (ok) { toast.success("تم تصفير القضية — بدأت من الصفر"); setConfirmFullReset(false); setRevealData(null); } }} data-testid="full-reset-confirm" className="btn-primary text-xs flex-1">تأكيد الإعادة</button>
              <button onClick={() => setConfirmFullReset(false)} className="btn-ghost text-xs flex-1">تراجع</button>
            </div>
          )}
        </div>
      </div>}

      {/* Case Ranking after result */}
      <LeaderboardPanel title="ترتيب المحققين لهذه الجلسة" />
      <PodiumLauncher />

      {/* Ownership footer inside case */}
      <div className="text-center pt-4 border-t border-metal/40">
        <div className="text-[10px] text-gold tracking-[0.3em] font-cairo">OK FBI INVESTIGATION GAME</div>
        <div className="text-[9px] text-zinc-500 mt-1 font-cairo">CREATED BY // AMW</div>
      </div>
    </div>
  );
}

function TruthRevealDetails({ caseId, data }) {
  const [r, setR] = useState(data);
  useEffect(() => {
    if (!r) api.post(`/session/case-result/${caseId}/reveal`).then(x => setR(x.data)).catch(() => {});
  }, [caseId, r]);
  if (!r) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="panel gold-border p-6" data-testid="truth-reveal">
      <div className="text-center mb-4">
        <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">CLASSIFIED · REVEALED</div>
        <div className="font-cairo text-2xl text-white mt-1">الحقيقة الكاملة</div>
      </div>
      {r.narrative && (r.narrative.summary || (r.narrative.beats || []).length > 0) && (
        <div className="mb-6 border-b border-metal pb-5" data-testid="reveal-narrative">
          {r.narrative.summary && (
            <p className="text-sm text-zinc-300 leading-relaxed text-center mb-4">{r.narrative.summary}</p>
          )}
          <div className="space-y-2">
            {(r.narrative.beats || []).map((b, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`reveal-beat-${i}`}>
                <div className="text-[10px] text-gold font-mono-ar pt-0.5 shrink-0 w-6 text-center">{i + 1}</div>
                <div className="text-xs text-zinc-400 leading-relaxed">
                  {b.name && <span className="text-white font-cairo">{b.name}: </span>}
                  {b.text}
                </div>
              </div>
            ))}
          </div>
          {(r.narrative.key_evidence || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
              <span className="text-[10px] text-zinc-500 self-center">الأدلة الحاسمة:</span>
              {r.narrative.key_evidence.map(e => (
                <span key={e.id} className="px-2 py-0.5 text-[10px] border border-gold/50 text-gold rounded-sm">{e.name}</span>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="text-center">
          <div className="w-36 h-36 mx-auto border-2 border-red-800 rounded-sm overflow-hidden bg-zinc-900">
            <img
              src={assetUrl(r.culprit?.portrait) || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"}
              className="w-full h-full object-cover"
              alt={r.culprit?.name || "المتهم الحقيقي"}
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces"; }}
            />
          </div>
          <div className="mt-3 text-xs text-red-400 tracking-widest font-mono-ar">المتهم الحقيقي</div>
          <div className="font-cairo text-2xl text-white">{r.culprit?.name || r.culprit?.id || "—"}</div>
          <div className="text-xs text-zinc-500 mt-1">{r.culprit?.job ? `${r.culprit.job} · ` : ""}{r.culprit?.id}</div>
        </div>
        <div className="md:col-span-2 space-y-3">
          <DetailBlock title="الدافع" content={r.motive || r.culprit?.motive || "غير معلوم"} />
          <DetailBlock title="طريقة الجريمة" content={r.method} />
          {r.primary_evidence && <DetailBlock title="الدليل الحاسم" content={r.primary_evidence.name} sub={r.primary_evidence.desc} />}
          <DetailBlock title="التناقض" content={r.key_contradiction} />
          <DetailBlock title="كيف أُدين" content={r.explanation} />
        </div>
      </div>
    </motion.div>
  );
}

// ================== HOST CONTROLS ==================
function HostControlsForCase({ c, session, caseId, tab, revealed, onReveal, onUnreveal, onRevealAll }) {
  const { updateSession, castVote, clearVote, resetCase, undoLastReveal } = useSession();
  const voting = session?.voting_status || "NOT_STARTED";
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const setVoting = (s) => { updateSession({ voting_status: s }); if (s === "CLOSED") sfx.voteLock(); else sfx.click(); };
  const changeStage = (stage) => { updateSession({ stage }); sfx.pageFlip(); };
  const history = session?.reveal_history || [];
  const lastReveal = history[0];

  return (
    <div className="space-y-4 text-sm">
      {/* Undo bar - most important host safety feature */}
      <div className="bg-red-950/20 border border-red-900/40 rounded-sm p-2.5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-widest text-red-300 font-mono-ar flex items-center gap-1.5"><History size={11} /> آخر كشف</div>
          <span className="text-[9px] text-zinc-500 font-mono-ar">{history.length} في السجل</span>
        </div>
        {lastReveal ? (
          <>
            <div className="text-[11px] text-white truncate mb-2">
              <span className="text-gold">{lastReveal.kind}</span> · {lastReveal.item_id}
            </div>
            <button onClick={async () => { await undoLastReveal(); }} data-testid="undo-last-reveal"
              className="w-full flex items-center justify-center gap-2 py-1.5 text-[11px] text-red-300 border border-red-800 hover:bg-red-900/40 rounded-sm">
              <Undo2 size={12} /> تراجع عن آخر كشف
            </button>
          </>
        ) : (
          <div className="text-[10px] text-zinc-600 italic">لم تكشف شيئاً بعد.</div>
        )}
      </div>

      {/* Stage nav */}
      <div>
        <div className="label-caps mb-2">مرحلة القضية</div>
        <div className="grid grid-cols-2 gap-1">
          {[["briefing","التمهيد"],["scene","الموقع"],["suspects","المشتبه"],["evidence","الأدلة"],["interrogation","الاستجواب"],["cctv","الكاميرات"],["forensics","التحليل"],["voting","التصويت"],["reveal","الكشف"]].map(([k, l]) => (
            <button key={k} onClick={() => changeStage(k)}
              className={`text-[11px] px-2 py-1 border rounded-sm ${session?.stage === k ? "border-gold text-gold bg-[rgba(197,160,89,0.1)]" : "border-metal text-zinc-400 hover:border-gold"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Reveal current tab items */}
      <div>
        <div className="label-caps mb-2">كشف عناصر — {TABS.find(t => t.id === tab)?.label}</div>
        <RevealList c={c} caseId={caseId} tab={tab} revealed={revealed} onReveal={onReveal} onUnreveal={onUnreveal} onRevealAll={onRevealAll} />
      </div>

      {/* Voting controls */}
      <div className="border-t border-metal pt-3">
        <div className="label-caps mb-2">التصويت</div>
        <div className="text-[10px] text-zinc-500 mb-2">الحالة: <span className="text-gold font-mono-ar">{voting}</span></div>
        <VotingOpenButton onOpen={() => setVoting("OPEN")} />
        <div className="grid grid-cols-2 gap-1 mb-2 mt-1">
          <button onClick={() => setVoting("PAUSED")} data-testid="voting-pause" className="btn-ghost text-[11px] py-1"><Pause size={10} className="inline" /> إيقاف</button>
          {!confirmClose ? (
            <button onClick={() => setConfirmClose(true)} data-testid="voting-close-init" className="btn-ghost text-[11px] py-1"><Square size={10} className="inline" /> إغلاق</button>
          ) : (
            <button onClick={() => { setVoting("CLOSED"); setConfirmClose(false); }} data-testid="voting-close-confirm" className="btn-primary text-[10px] py-1">تأكيد الإغلاق</button>
          )}
        </div>
        {confirmClose && (
          <button onClick={() => setConfirmClose(false)} className="text-[10px] text-zinc-500 w-full mb-2">إلغاء</button>
        )}
        <VoteTally caseId={caseId} suspects={c.suspects} />
        <VoteAssigner suspects={c.suspects} disabled={voting !== "OPEN"} />
      </div>

      <div className="border-t border-metal pt-3">
        {!confirmReset ? (
          <button onClick={() => setConfirmReset(true)} data-testid="reset-session-init" className="btn-ghost text-[11px] w-full py-1 text-red-400 border-red-900/50 hover:bg-red-950/30">إعادة تعيين التحقيق</button>
        ) : (
          <div className="p-2 border border-red-900 rounded-sm text-[11px]">
            <div className="text-red-300 mb-1">سيتم مسح جميع الكشوفات والأصوات. المشاركون سيبقون.</div>
            <div className="flex gap-1">
              <button onClick={async () => { const ok = await resetCase(caseId); if (ok) { toast.success("تم تصفير التحقيق"); setConfirmReset(false); } }} data-testid="reset-session-confirm" className="btn-primary text-[10px] flex-1 py-1">نعم</button>
              <button onClick={() => setConfirmReset(false)} className="btn-ghost text-[10px] flex-1 py-1">تراجع</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RevealList({ c, caseId, tab, revealed, onReveal, onUnreveal, onRevealAll }) {
  const kindMap = {
    suspects: { kind: "suspects", items: (c?.suspects || []).map(s => ({ id: s.id, label: `${s.name} — ${s.job || ''}` })) },
    evidence: { kind: "evidence", items: (c?.evidence || []).map(e => ({ id: e.id, label: `${e.id} · ${e.name}` })) },
    cctv: { kind: "cctv", items: (c?.cctv || c?.cctv_cameras || []).map(x => ({ id: x.id, label: `${x.id} · ${x.location || x.name || ''}` })) },
    timeline: { kind: "timeline", items: (c?.timeline || []).map((t, i) => ({ id: String(i), label: `${t.time || t.at || ''} · ${(t.event || '').slice(0, 30)}` })) },
    locations: { kind: "locations", items: (c?.locations || []).map(l => ({ id: l.id, label: l.name })) },
    forensics: { kind: "forensics", items: (c?.evidence || []).filter(e => e.analysis).map(e => ({ id: e.id, label: `${e.id} · ${e.analysis.tool}` })) },
    interrogations: { kind: "interrogations", items: (c?.suspects || []).map(s => ({ id: s.id, label: s.name })) },
  };
  const entry = kindMap[tab];
  if (!entry) return <div className="text-[11px] text-zinc-600">لا توجد عناصر في هذه العلامة.</div>;
  const rev = new Set(revealed[entry.kind] || []);
  return (
    <div>
      <button onClick={() => onRevealAll(caseId, entry.kind)} data-testid={`reveal-all-${entry.kind}`}
        className="btn-ghost text-[10px] w-full py-1 mb-2">كشف الكل ({entry.items.length})</button>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {entry.items.map((it) => {
          const on = rev.has(it.id);
          return (
            <button key={it.id} onClick={() => on ? onUnreveal(caseId, entry.kind, it.id) : onReveal(caseId, entry.kind, it.id)}
              data-testid={`reveal-${entry.kind}-${it.id}`}
              className={`w-full text-right px-2 py-1 text-[11px] flex items-center gap-2 border rounded-sm ${on ? "border-cyan-800 bg-cyan-950/20 text-cyan-300" : "border-metal text-zinc-400 hover:border-gold"}`}>
              {on ? <Eye size={11} /> : <EyeOff size={11} />}
              <span className="flex-1 truncate">{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function VoteTally({ caseId, suspects }) {
  const [tally, setTally] = useState({ tally: [], total_votes: 0 });
  useEffect(() => {
    const load = () => api.get(`/session/votes/${caseId}/tally`).then(r => setTally(r.data)).catch(() => {});
    load(); const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [caseId]);
  if (!tally.total_votes) return <div className="text-[10px] text-zinc-600 mt-2">لا توجد أصوات بعد.</div>;
  return (
    <div className="mt-3 space-y-1" data-testid="vote-tally">
      <div className="text-[10px] text-zinc-500">إجمالي الأصوات: <span className="text-gold">{tally.total_votes}</span></div>
      {tally.tally.map((t) => {
        const s = suspects.find(x => x.id === t.suspect_id);
        return (
          <div key={t.suspect_id} className="text-[11px]">
            <div className="flex justify-between text-white"><span>{s?.name || t.suspect_id}</span><span className="text-gold font-mono-ar">{t.pct}%</span></div>
            <div className="h-1 bg-zinc-900 rounded overflow-hidden mt-0.5"><div className="h-full bg-gradient-to-l from-yellow-500 to-yellow-700" style={{ width: `${t.pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function VoteAssigner({ suspects, disabled }) {
  const { session, castVote, clearVote } = useSession();
  const [pname, setPname] = useState("");
  const parts = session?.participants || [];
  const votes = session?.votes?.[session?.current_case_id] || {};
  const assign = async (sid) => { if (pname) await castVote(pname, sid); };
  return (
    <div className="mt-3 border-t border-metal pt-2">
      <div className="text-[10px] text-zinc-500 mb-1">تعيين تصويت مشارك:</div>
      <select value={pname} onChange={(e) => setPname(e.target.value)} data-testid="vote-participant-select"
        className="w-full bg-black border border-metal text-[11px] text-white px-1 py-1 rounded-sm mb-1">
        <option value="">اختر مشاركاً...</option>
        {parts.map(p => <option key={p.name} value={p.name}>{p.name} {votes[p.name] ? `→ ${votes[p.name]}` : ""}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-1">
        {suspects.map(s => (
          <button key={s.id} onClick={() => assign(s.id)} disabled={disabled || !pname}
            data-testid={`vote-for-${s.id}`}
            className="text-[10px] px-1 py-1 border border-metal rounded-sm hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed truncate">
            {s.name}
          </button>
        ))}
      </div>
      {pname && votes[pname] && (
        <button onClick={() => clearVote(pname)} className="mt-1 text-[10px] text-red-400 hover:text-red-300 w-full text-center" data-testid="clear-vote">إلغاء تصويت {pname}</button>
      )}
    </div>
  );
}

function PodiumLauncher() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="panel border-gold p-4 flex items-center justify-between" data-testid="podium-launcher">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">FBI AWARD CEREMONY</div>
          <div className="font-cairo text-xl text-white mt-1">مراسم تكريم أفضل ثلاثة محققين</div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="podium-open" className="btn-primary">فتح المسرح</button>
      </div>
      <CaseRankingPodium open={open} onClose={() => setOpen(false)} />
    </>
  );
}


// TikTok-aware voting-open button. If the TikTok bridge is NOT connected/live it
// refuses to silently open a TikTok vote and offers the host a truthful choice:
// reconnect the bridge OR proceed with manual (in-app) voting only.
function VotingOpenButton({ onOpen }) {
  const { user, isOwner } = useAuth();
  const isStreamerOrOwner = isOwner || (user?.membership_plan || "").toUpperCase() === "STREAMER" || (user?.role || "").toUpperCase() === "STREAMER";
  const [conn, setConn] = useState(null);
  const [askManual, setAskManual] = useState(false);
  useEffect(() => {
    if (!isStreamerOrOwner) return;
    let stop = false;
    const load = () => api.get("/tiktok/status").then(({ data }) => { if (!stop) setConn(data); }).catch(() => {});
    load(); const iv = setInterval(load, 2500);
    return () => { stop = true; clearInterval(iv); };
  }, [isStreamerOrOwner]);
  const connected = !!conn?.connected;

  const handleClick = () => {
    if (connected) { onOpen(); return; }
    setAskManual(true);
  };
  const reconnect = async () => {
    try { await api.post("/tiktok/reconnect"); setAskManual(false); }
    catch { /* silent */ }
  };
  const manual = () => { setAskManual(false); onOpen(); };

  return (
    <>
      <button onClick={handleClick} data-testid="voting-open"
        className="btn-primary text-[11px] w-full py-1 flex items-center justify-center gap-1">
        <Play size={10} /> فتح التصويت
      </button>
      {askManual && (
        <div className="mt-2 p-2 border border-amber-800 bg-amber-950/20 rounded-sm text-[11px] font-cairo" data-testid="voting-tiktok-guard">
          <div className="text-amber-300 mb-1">تعذر فتح تصويت TikTok</div>
          <div className="text-zinc-400 mb-2">الاتصال بتعليقات البث غير متاح.</div>
          <div className="grid grid-cols-2 gap-1">
            <button onClick={reconnect} className="text-[11px] border border-gold text-gold py-1 hover:bg-[rgba(197,160,89,0.12)]" data-testid="voting-guard-reconnect">إعادة الاتصال</button>
            <button onClick={manual} className="text-[11px] border border-metal text-zinc-300 py-1 hover:border-gold hover:text-gold" data-testid="voting-guard-manual">تصويت يدوي</button>
          </div>
        </div>
      )}
    </>
  );
}

// ================== OPERATIONS ROOM TAB (Global — auto-binds to current case) ==================
// Central command panel: Real TikTok LIVE connection, live comments feed, live vote tally,
// OBS mirror toggle. TikTok connection is GLOBAL (backend session) — closing this tab or
// switching cases NEVER disconnects the bridge.
function OperationsRoomTab({ c, caseId }) {
  const { session, updateSession } = useSession();
  const { user, isOwner } = useAuth();
  const canManageBroadcast = Boolean(isOwner || String(user?.role || "").toUpperCase() === "STREAMER" || String(user?.membership_plan || "").toUpperCase() === "STREAMER");
  const [showFeed, setShowFeed] = useState(true);
  const [obsCfg, setObsCfg] = useState({});
  useEffect(() => {
    let stop = false;
    const load = () => api.get("/overlay/state").then(({ data }) => { if (!stop) setObsCfg(data?.config || {}); }).catch(() => {});
    load(); const iv = setInterval(load, 5000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  const toggleObsMirror = async () => {
    const next = !obsCfg.show_meeting_room;
    try { await api.patch("/session/overlay-config", { show_meeting_room: next }); setObsCfg(x => ({ ...x, show_meeting_room: next })); }
    catch {}
  };

  return (
    <div className="space-y-4" data-testid="operations-room">
      <div className="panel p-4 border-metal">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Radio size={16} className="text-gold" />
            <div className="text-[13px] tracking-[0.25em] text-gold font-cairo">غرفة العمليات</div>
          </div>
          <div className="text-[10px] font-mono-ar text-zinc-500">
            القضية الحالية: <span className="text-gold">{c?.code || caseId}</span>
          </div>
        </div>
        <div className="text-[10px] text-zinc-500 font-cairo">مركز متابعة البث والتصويت المباشر</div>
      </div>

      {/* Multi-Platform Broadcast Manager (Kick · Twitch · TikTok) */}
      <StreamPlatformsManager />

      {/* Connection strip */}
      <TikTokConnectStrip caseId={caseId} suspects={c.suspects} />

      {/* Feed toggle + OBS mirror toggle */}
      <div className="panel p-3 border-metal flex items-center gap-3 flex-wrap" data-testid="ops-room-toggles">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-400 font-cairo">عرض التعليقات:</span>
          <button onClick={() => setShowFeed(v => !v)} data-testid="ops-feed-toggle"
            className={`text-[11px] px-2 py-1 border ${showFeed ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400 hover:border-gold hover:text-gold"}`}>
            {showFeed ? "تشغيل" : "إيقاف"}
          </button>
        </div>
        <div className="flex items-center gap-2 mr-auto">
          <span className="text-[11px] text-zinc-400 font-cairo">إظهار غرفة العمليات في البث:</span>
          <button onClick={toggleObsMirror} data-testid="ops-obs-toggle" disabled={!canManageBroadcast}
            title={canManageBroadcast ? "" : "إظهار غرفة العمليات في البث متاح لباقة Streamer والمالك فقط"}
            className={`text-[11px] px-2 py-1 border ${canManageBroadcast ? (obsCfg.show_meeting_room ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal text-zinc-400 hover:border-gold hover:text-gold") : "border-metal text-zinc-600 opacity-60 cursor-not-allowed"}`}>
            {obsCfg.show_meeting_room ? "تشغيل" : "إيقاف"}
          </button>
        </div>
      </div>

      {/* Comments room (only when feed toggle ON — TikTok stays connected either way) */}
      {showFeed && <InvestigatorMeetingRoom caseId={caseId} suspects={c.suspects} mini={false} />}
      {!showFeed && (
        <div className="panel p-4 border-metal text-center text-[11px] text-zinc-500 font-cairo" data-testid="ops-feed-off">
          عرض التعليقات معطّل مؤقتاً · TikTok لا يزال متصلاً في الخلفية
        </div>
      )}

      {/* Live results */}
      <OpsLiveResults caseId={caseId} suspects={c.suspects} session={session} />
    </div>
  );
}

// Live results — per-case isolated. Reads votes[caseId] from session and shows counts + %.
function OpsLiveResults({ caseId, suspects, session }) {
  const votes = (session?.votes || {})[caseId] || {};
  const voting = session?.voting_status || "NOT_STARTED";
  const total = Object.keys(votes).length;
  const counts = {};
  Object.values(votes).forEach(sid => { counts[sid] = (counts[sid] || 0) + 1; });
  const rows = (suspects || []).map((s, i) => {
    const cnt = counts[s.id] || 0;
    return {
      idx: i + 1, id: s.id, name: s.name,
      count: cnt,
      pct: total > 0 ? Math.round((cnt / total) * 100) : 0,
    };
  }).sort((a, b) => b.count - a.count);

  const recent = (session?.recent_votes || []).filter(r => r.case_id === caseId).slice(0, 1)[0];

  return (
    <div className="panel p-4 border-metal" data-testid="ops-live-results">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] tracking-[0.25em] text-gold font-cairo">التصويت المباشر</div>
        <div className={`text-[10px] font-mono-ar px-2 py-0.5 border ${voting === "OPEN" ? "text-red-300 border-red-800 bg-red-950/20" : "text-zinc-500 border-metal"}`}>
          {voting === "OPEN" ? "🔴 التصويت مباشر" : voting === "CLOSED" ? "التصويت مغلق" : voting === "PAUSED" ? "التصويت متوقف" : "لم يبدأ"}
        </div>
      </div>
      <div className="space-y-1.5 mb-3">
        {rows.map(r => (
          <div key={r.id} className="flex items-center gap-2 text-[11px] font-cairo" data-testid={`ops-suspect-row-${r.id}`}>
            <span className="text-gold font-mono-ar w-6 text-center">#{r.idx}</span>
            <span className="text-white flex-1 truncate">{r.name}</span>
            <span className="text-zinc-500 font-mono-ar tabular-nums w-10 text-left">{r.count}</span>
            <div className="h-1.5 w-24 bg-black/40 border border-metal/40 relative overflow-hidden">
              <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${Math.min(100, r.pct)}%` }} />
            </div>
            <span className="text-gold font-mono-ar tabular-nums w-10 text-left">{r.pct}%</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 text-[10px] font-cairo pt-2 border-t border-metal/40">
        <div className="text-zinc-500">إجمالي المصوتين: <span className="text-white font-mono-ar tabular-nums">{total}</span></div>
        {recent && (
          <div className="text-zinc-500 mr-auto truncate">
            آخر تصويت: <span className="text-gold">@{recent.participant}</span> → <span className="text-white font-mono-ar">{(suspects.findIndex(s => s.id === recent.suspect_id) + 1) || "?"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

